/* eslint-disable react-refresh/only-export-components */
/**
 * NativeSSOProvider — React Context pour @ollaid/native-sso
 * Centralise la configuration pour les hooks et composants individuels
 * 
 * @version 2.7.9
 */

import { createContext, useContext, useEffect, useRef, type ReactNode } from 'react';
import { setNativeAuthConfig, setNativeStorage, type NativeAuthConfig, type NativeStorageAdapter } from './services/api';

export interface NativeSSOConfig {
  /** URL du Backend SaaS (ex: https://mon-saas.com/api) */
  saasApiUrl: string;
  /** URL du Backend IAM (ex: https://identityam.ollaid.com/api) */
  iamApiUrl: string;
  /** Timeout en ms (défaut: 30000) */
  timeout?: number;
  /** Mode debug (logs console) */
  debug?: boolean;
  /** Stockage injectable pour les apps natives / Capacitor */
  storage?: NativeStorageAdapter;
}

const NativeSSOContext = createContext<NativeSSOConfig | null>(null);

export interface NativeSSOProviderProps {
  config: NativeSSOConfig;
  children: ReactNode;
}

/**
 * Provider React pour centraliser la config SSO.
 * 
 * Usage :
 * ```tsx
 * <NativeSSOProvider config={{ saasApiUrl: '...', iamApiUrl: '...' }}>
 *   <App />
 * </NativeSSOProvider>
 * ```
 */
export function NativeSSOProvider({ config, children }: NativeSSOProviderProps) {
  const configuredRef = useRef(false);

  if (config.storage) {
    setNativeStorage(config.storage);
  }

  useEffect(() => {
    if (!configuredRef.current) {
      setNativeAuthConfig(config);
      configuredRef.current = true;
    }
  }, [config]);

  return (
    <NativeSSOContext.Provider value={config}>
      {children}
    </NativeSSOContext.Provider>
  );
}

/**
 * Hook pour accéder à la config SSO depuis le Provider.
 * Optionnel : les hooks acceptent aussi la config en paramètre.
 */
export function useNativeSSOConfig(): NativeSSOConfig {
  const ctx = useContext(NativeSSOContext);
  if (!ctx) {
    throw new Error('useNativeSSOConfig doit être utilisé dans un <NativeSSOProvider>');
  }
  return ctx;
}

export default NativeSSOProvider;
